/**
 * Settings page.
 *  - Always shown: change-my-password form.
 *  - Owner-only: invite users, list users, revoke / reinstate / reset / delete.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import * as adminApi from '../api/admin';
import { ApiError } from '../api/client';
import type { ManagedUser } from '../types';
import AppHeader from '../components/AppHeader';

export default function SettingsPage() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <>
      <AppHeader subtitle="Settings" />
      <div className="main">
        <div className="page-eyebrow">Account</div>
        <div className="page-title">Settings</div>
        <div className="page-sub">Account password and (for owners) team management.</div>

        <ChangePasswordCard />

        {user.role === 'owner' && <TeamManagementCard />}
      </div>
    </>
  );
}

function ChangePasswordCard() {
  const { show } = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (next !== confirm) {
      setErr('Passwords do not match.');
      return;
    }
    if (next.length < 12) {
      setErr('New password must be at least 12 characters.');
      return;
    }
    setBusy(true);
    try {
      await adminApi.changeMyPassword(current, next);
      show('Password changed');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (e) {
      const msg =
        e instanceof ApiError ? (e.body as { error?: string } | null)?.error : null;
      setErr(msg ?? 'Failed to change password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">Password</span>
      </div>
      <div className="card-body">
        <form className="field-grid fg-3" onSubmit={submit}>
          <div className="field">
            <label className="field-label">Current password</label>
            <input
              className="field-input"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label className="field-label">New password</label>
            <input
              className="field-input"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={12}
            />
          </div>
          <div className="field">
            <label className="field-label">Confirm</label>
            <input
              className="field-input"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={12}
            />
          </div>
          {err && <div className="login-error span2">{err}</div>}
          <div>
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Change password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TeamManagementCard() {
  const { show } = useToast();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [resetResult, setResetResult] = useState<{ email: string; temp_password: string } | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const { users } = await adminApi.listUsers();
      setUsers(users);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    refresh();
  }, []);

  async function onRevoke(u: ManagedUser) {
    if (!confirm(`Revoke access for ${u.email}?`)) return;
    await adminApi.revokeUser(u.id);
    show('Access revoked');
    refresh();
  }
  async function onReinstate(u: ManagedUser) {
    await adminApi.reinstateUser(u.id);
    show('User reinstated');
    refresh();
  }
  async function onReset(u: ManagedUser) {
    if (!confirm(`Reset password for ${u.email}? They will be forced to change it on next login.`)) return;
    const { temp_password } = await adminApi.adminResetPassword(u.id);
    if (temp_password) setResetResult({ email: u.email, temp_password });
    refresh();
  }
  async function onDelete(u: ManagedUser) {
    if (!confirm(`Permanently disable ${u.email}? Their batches stay; their account is closed.`)) return;
    await adminApi.deleteUser(u.id);
    show('User deleted');
    refresh();
  }
  async function onChangeRole(u: ManagedUser, role: 'owner' | 'editor' | 'viewer') {
    try {
      await adminApi.patchUser(u.id, { role });
      show('Role updated');
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body as { error?: string } | null)?.error ?? e.message
          : 'Failed to update role';
      alert(msg);
    }
    refresh();
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-header">
        <span className="card-title">Team — owner only</span>
        <button className="btn btn-primary btn-sm" onClick={() => setInviteOpen(true)}>
          + Invite user
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--ink3)' }}>Loading…</div>}
        {!loading && users.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                <th style={th}>Email</th>
                <th style={th}>Name</th>
                <th style={th}>Role</th>
                <th style={th}>Status</th>
                <th style={th}>Last login</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={td}>{u.email}</td>
                  <td style={td}>{u.display_name}</td>
                  <td style={td}>
                    <select
                      className="field-select"
                      value={u.role}
                      onChange={(e) =>
                        onChangeRole(u, e.target.value as 'owner' | 'editor' | 'viewer')
                      }
                    >
                      <option value="owner">owner</option>
                      <option value="editor">editor</option>
                      <option value="viewer">viewer</option>
                    </select>
                  </td>
                  <td style={td}>
                    {u.status}
                    {u.must_change_password ? ' (pw reset pending)' : ''}
                  </td>
                  <td style={td}>
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleString()
                      : '—'}
                  </td>
                  <td style={td}>
                    {u.status === 'active' && (
                      <>
                        <button className="btn btn-ghost btn-sm" style={{ borderColor: 'var(--border)', color: 'var(--ink2)' }} onClick={() => onReset(u)}>
                          Reset pw
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => onRevoke(u)} style={{ marginLeft: 4 }}>
                          Revoke
                        </button>
                      </>
                    )}
                    {u.status === 'disabled' && (
                      <>
                        <button className="btn btn-primary btn-sm" onClick={() => onReinstate(u)}>
                          Reinstate
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => onDelete(u)} style={{ marginLeft: 4 }}>
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {inviteOpen && (
        <InviteModal
          onClose={() => {
            setInviteOpen(false);
            // The user may close the modal after seeing the temp password
            // instead of clicking Done. Refresh either way so the new row
            // appears immediately.
            refresh();
          }}
          onDone={() => {
            setInviteOpen(false);
            refresh();
          }}
        />
      )}

      {resetResult && (
        <TempPasswordModal
          email={resetResult.email}
          tempPassword={resetResult.temp_password}
          onClose={() => setResetResult(null)}
        />
      )}
    </div>
  );
}

function TempPasswordModal({
  email,
  tempPassword,
  onClose,
}: {
  email: string;
  tempPassword: string;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">New temporary password</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 12 }}>
            For <strong>{email}</strong>:
          </div>
          <div className="field-label">Temporary password</div>
          <pre
            style={{
              background: 'var(--surface2)',
              padding: '8px 10px',
              borderRadius: 5,
              fontSize: 13,
              userSelect: 'all',
            }}
          >
            {tempPassword}
          </pre>
          <div style={{ marginTop: 12, fontSize: 11, color: 'var(--ink3)' }}>
            Share this via a secure channel (Signal, in person, etc.) — not email.
            The user will be forced to change it on next login.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: 'var(--ink3)',
};
const td: React.CSSProperties = { padding: '8px 12px', color: 'var(--ink2)' };

function InviteModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'owner' | 'editor' | 'viewer'>('editor');
  const [mode, setMode] = useState<'temp_password' | 'invite_link'>('temp_password');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    temp_password: string | null;
    invite_url: string | null;
  } | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await adminApi.inviteUser({
        email: email.trim(),
        display_name: name.trim(),
        role,
        mode,
      });
      setResult({
        temp_password: r.temp_password,
        invite_url: r.invite_url,
      });
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'failed';
      alert(`Invite failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Invite a teammate</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
        {!result ? (
          <form className="modal-body" onSubmit={submit}>
            <div className="field-grid fg-2">
              <div className="field">
                <label className="field-label">Email</label>
                <input
                  className="field-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="field">
                <label className="field-label">Display name</label>
                <input
                  className="field-input"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="field">
                <label className="field-label">Role</label>
                <select className="field-select" value={role} onChange={(e) => setRole(e.target.value as 'owner' | 'editor' | 'viewer')}>
                  <option value="editor">editor</option>
                  <option value="viewer">viewer</option>
                  <option value="owner">owner</option>
                </select>
              </div>
              <div className="field">
                <label className="field-label">Invite type</label>
                <select className="field-select" value={mode} onChange={(e) => setMode(e.target.value as 'temp_password' | 'invite_link')}>
                  <option value="temp_password">Generate temp password</option>
                  <option value="invite_link">Generate invite link</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink3)' }}>
              Temp password: I'll show you a one-time string to share. The user will be forced to change it on first login.
              <br />
              Invite link: I'll give you a URL valid for 7 days. The user clicks it and sets their own password.
            </div>
          </form>
        ) : (
          <div className="modal-body">
            <div style={{ marginBottom: 12, fontWeight: 500 }}>Share this with the new user:</div>
            {result.temp_password && (
              <>
                <div className="field-label">Temporary password</div>
                <pre style={{ background: 'var(--surface2)', padding: '8px 10px', borderRadius: 5, fontSize: 13, userSelect: 'all' }}>{result.temp_password}</pre>
              </>
            )}
            {result.invite_url && (
              <>
                <div className="field-label">Invite link</div>
                <pre style={{ background: 'var(--surface2)', padding: '8px 10px', borderRadius: 5, fontSize: 12, wordBreak: 'break-all', userSelect: 'all' }}>{result.invite_url}</pre>
              </>
            )}
            <div style={{ marginTop: 12, fontSize: 11, color: 'var(--ink3)' }}>
              Send this via a secure channel (Signal, in person, etc.) — not email.
            </div>
          </div>
        )}
        <div className="modal-footer">
          {!result ? (
            <>
              <button className="btn btn-white" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={submit} disabled={busy || !email || !name}>
                {busy ? 'Creating…' : 'Send invite'}
              </button>
            </>
          ) : (
            <button className="btn btn-primary" onClick={onDone}>Done</button>
          )}
        </div>
      </div>
    </div>
  );
}
